import { ReactNode, useState, useEffect } from "react"
import { useQuery } from "@tanstack/react-query"
import { I18nProvider } from "@lingui/react"
import { i18n } from "@lingui/core"
import { useAppStore } from "@src/state"

import { getCommunityLiteralsQuery } from "@src/api/literals"
import { getCurrentUserQuery } from "@src/api/user"
import { useCommunityId } from "@src/hooks/useCommunityId"
import { formatLanguages } from "./utils"
import { LoadingIndicator } from "@src/components/atoms/LoadingIndicator"
import { localesMap } from "@src/utils/language"

const getBrowserLanguage = () => {
  const browserLanguage = navigator.language?.slice(0, 2) ?? "en"
  return browserLanguage in localesMap ? browserLanguage : "en"
}

export const LanguageApplicationWrapper = ({
  children,
}: {
  children: ReactNode
}) => {
  const [languageActivated, setLanguageActivated] = useState(false)
  const communityId = useCommunityId()
  const token = useAppStore.getState().token
  const isAuthenticated = !!token

  // Only fetch when authenticated — unauthenticated users use browser language
  const { data: communityLiterals } = useQuery({
    ...getCommunityLiteralsQuery(communityId),
    enabled: isAuthenticated,
  })

  const { data: currentUser } = useQuery({
    ...getCurrentUserQuery(),
    enabled: isAuthenticated,
  })

  const canActivateLanguage =
    !isAuthenticated || (!!currentUser && !!communityLiterals)

  useEffect(() => {
    if (!canActivateLanguage) return

    const activate = async () => {
      const language =
        currentUser?.language?.slice(0, 2) ?? getBrowserLanguage()

      const replacements = {
        "100": communityLiterals?.primitive_startup ?? "Startup",
        "101": communityLiterals?.primitive_startups ?? "Startups",
        "200": communityLiterals?.primitive_program ?? "Program",
        "201": communityLiterals?.primitive_programs ?? "Programs",
      }

      const { messages } = await import(`@src/locales/${language}/messages.ts`)

      const mergedMessages = {
        ...messages,
        ...communityLiterals,
      }

      const processedMessages = formatLanguages(mergedMessages, replacements)
      i18n.load(language, processedMessages)
      i18n.activate(language)
      setLanguageActivated(true)
    }

    activate()
  }, [canActivateLanguage, currentUser, communityLiterals])

  if (!languageActivated && isAuthenticated) {
    return <LoadingIndicator />
  }

  return <I18nProvider i18n={i18n}>{children}</I18nProvider>
}
